// ---------------------------------------------------------------------------
// White-label — แบรนด์ของร้านแทน LucidCRM
// ---------------------------------------------------------------------------
// เก็บที่ organizations.branding = { displayName, logo (data URL ≤256px), color (#rrggbb) }
// ใช้แบรนด์ของร้านได้เฉพาะตอนแพ็กเกจหลักยังไม่หมดอายุ และมีสิทธิ์โมดูล white_label
// (อยู่ในแพ็กเกจ Enterprise Plus หรือซื้อฟีเจอร์เสริม) — หมดสิทธิ์ = กลับไปแบรนด์ LucidCRM ทันที
// ทุกหน้าที่ต้องแสดงชื่อ/โลโก้ ให้ใช้ brandingOf(organization) / useBranding() ตัวนี้ ห้ามเขียนชื่อ LucidCRM ตรง ๆ
// ---------------------------------------------------------------------------
import { BRAND, resolveSubscription } from "./plans";
import { useApp } from "../context/AppContext";

export const BRANDING_MODULE = "white_label";
export const LOGO_MAX_PX = 256;
export const LOGO_MAX_CHARS = 45000; // เซลล์ Google Sheets เก็บได้ ~50,000 ตัวอักษร
export const NAME_MAX = 60;
const COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;
const LOGO_PATTERN = /^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/;

export const validColor = (value) => COLOR_PATTERN.test(String(value || "").trim());
export const validLogo = (value) => typeof value === "string" && value.length <= LOGO_MAX_CHARS && LOGO_PATTERN.test(value);

/** ทำความสะอาดค่าที่จะบันทึก — ตัดค่าไม่ถูกต้องทิ้ง */
export const sanitizeBranding = (input) => ({
  displayName: String(input?.displayName || "").trim().slice(0, NAME_MAX),
  logo: validLogo(input?.logo) ? input.logo : "",
  color: validColor(input?.color) ? String(input.color).trim().toLowerCase() : "",
});

/** ปรับสีให้เข้มลง (ใช้ทำไล่เฉดโลโก้) */
const darken = (hex, ratio = 0.25) => {
  const n = parseInt(hex.slice(1), 16);
  const channel = (shift) => Math.max(0, Math.round(((n >> shift) & 255) * (1 - ratio)));
  return `#${[16, 8, 0].map((shift) => channel(shift).toString(16).padStart(2, "0")).join("")}`;
};

const DEFAULT = {
  custom: false,
  name: BRAND.name,
  shortName: "LU",
  logo: "",
  color: "",
  cssVars: {},
};

/** ร้านนี้มีสิทธิ์ใช้แบรนด์ของตัวเองไหม (ยึดวันหมดอายุจริง) */
export const canWhiteLabel = (organization) => {
  if (!organization) return false;
  const sub = resolveSubscription(organization);
  return sub.isActive && sub.modules.includes(BRANDING_MODULE);
};

/**
 * แบรนด์ที่ต้องแสดง — { custom, name, shortName, logo, color, cssVars }
 *   custom=false -> LucidCRM ตามปกติ (ไม่มีสิทธิ์ / ยังไม่ตั้งค่า / ข้อมูลเสีย)
 *   cssVars ใส่เป็น style ที่ root ของหน้า เพื่อเปลี่ยนสีหลัก
 */
export const brandingOf = (organization) => {
  if (!canWhiteLabel(organization)) return DEFAULT;
  const saved = sanitizeBranding(organization.branding);
  const name = saved.displayName || organization.name || "";
  if (!saved.displayName && !saved.logo && !saved.color) return DEFAULT;
  if (!name) return DEFAULT;
  const cssVars = saved.color
    ? { "--c-indigo-500": saved.color, "--c-indigo-600": saved.color, "--gradient-brand": `linear-gradient(135deg, ${saved.color}, ${darken(saved.color)})` }
    : {};
  return {
    custom: true,
    name,
    shortName: name.trim().charAt(0).toUpperCase() || "?",
    logo: saved.logo,
    color: saved.color,
    cssVars,
  };
};

/** แบรนด์ของร้านที่ล็อกอินอยู่ */
export const useBranding = () => {
  const { organization } = useApp();
  return brandingOf(organization);
};
